/**
 * Cadastro: não existe formulário de "novo cliente" aqui — quem cadastra é
 * o próprio cliente, ao começar o briefing. O link é permanente (o mesmo
 * link sempre, reutilizável por quantos clientes precisar) até a Lara
 * clicar "Regenerar" — aí o antigo para de funcionar e um novo aparece.
 * Fonte: docs/00G §1, ajustado em 26/jul.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";

interface Invite {
  url: string;
  message: string;
  whatsappUrl: string;
  useCount: number;
  lastUsedAt: string | null;
}

export function Cadastro() {
  const [invite, setInvite] = useState<Invite | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api
      .get<Invite | null>("/invites/current")
      .then(setInvite)
      .finally(() => setLoading(false));
  }, []);

  async function gerar() {
    setGenerating(true);
    try {
      const res = await api.post<Invite>("/invites/regenerate", {});
      setInvite(res);
      setCopied(false);
      setLinkCopied(false);
    } finally {
      setGenerating(false);
    }
  }

  async function regenerar() {
    if (!window.confirm("Gerar um novo link? O link atual para de funcionar imediatamente.")) return;
    await gerar();
  }

  async function copiar() {
    if (invite === null) return;
    await navigator.clipboard.writeText(invite.message).catch(() => undefined);
    setCopied(true);
  }

  async function copiarLink() {
    if (invite === null) return;
    await navigator.clipboard.writeText(invite.url).catch(() => undefined);
    setLinkCopied(true);
  }

  return (
    <AdminShell>
      <h1 className="type-h1 text-text-cream">Cadastro</h1>
      <p className="type-body-sm mt-1 max-w-md text-text-secondary">
        Um cliente novo se cadastra sozinho: você manda o link do convite, ele cria a própria
        conta (nome, e-mail e senha) e já começa o diagnóstico — sem formulário pra você preencher.
      </p>

      <Card className="mt-6 max-w-md">
        <h2 className="type-h3 text-text-cream">Convite para clientes novos</h2>
        <p className="type-body-sm mt-2 text-text-secondary">
          Este link é permanente — o mesmo link serve pra quantos clientes você quiser convidar.
          Só muda se você regenerar.
        </p>

        {loading && <p className="type-caption mt-4 text-text-tertiary">Carregando…</p>}

        {!loading && invite === null && (
          <div className="mt-4">
            <Button onClick={gerar} loading={generating}>Gerar link de convite</Button>
          </div>
        )}

        {invite !== null && (
          <div className="mt-4 rounded-[var(--radius-control)] border border-border-subtle p-4">
            <p className="type-caption break-all text-text-secondary">{invite.url}</p>
            <p className="type-caption mt-2 text-text-tertiary">
              Usado {invite.useCount}× {invite.lastUsedAt !== null && `· último uso em ${new Date(invite.lastUsedAt).toLocaleDateString("pt-BR")}`}
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Button size="sm" onClick={copiarLink}>{linkCopied ? "Copiado!" : "Copiar link"}</Button>
              <Button size="sm" variant="secondary" onClick={copiar}>
                {copied ? "Copiado!" : "Copiar mensagem"}
              </Button>
              <a href={invite.whatsappUrl} target="_blank" rel="noreferrer">
                <Button size="sm" variant="secondary">Abrir no WhatsApp</Button>
              </a>
              <Button size="sm" variant="ghost" onClick={regenerar} loading={generating}>
                Regenerar link
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="mt-6 max-w-md">
        <h2 className="type-h3 text-text-cream">Formulário Público de Auto-Cadastro</h2>
        <p className="type-body-sm mt-2 text-text-secondary">
          Para testar a experiência cinematográfica do cliente ou cadastrar diretamente ao vivo:
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <a href="/cadastro" target="_blank" rel="noreferrer">
            <Button size="sm" variant="secondary">Abrir formulário /cadastro ↗</Button>
          </a>
        </div>
      </Card>
    </AdminShell>
  );
}
